import { lazy, Suspense } from 'react';
import { CheckoutField, PORTAL_CONTAINER_ID, usePlatformImageGetAssetSrc, TextKey } from '@mews-distributor/common';
import { LocalizedText } from '@mews/framework';
import { FieldProps } from '@optimus-web/core';
import { ErrorBoundary } from '../utils/ErrorBoundary';
import { useCheckoutField } from '../utils/hooks';

const PhoneInputField = lazy(async () =>
    import(/* webpackChunkName: "phoneInput" */ '@mews/business-components').then((module) => ({
        default: module.PhoneInputField,
    }))
);

interface CheckoutPhoneInputProps extends Pick<React.SuspenseProps, 'fallback'>, Pick<FieldProps, 'label'> {
    name: CheckoutField.BookerEmail | CheckoutField.BookerTelephone;
}

export const CheckoutPhoneInput = ({ name, label, fallback }: CheckoutPhoneInputProps) => {
    const { onBlur, error, customerValue, required, dataTestId, onChange } = useCheckoutField(name);
    const getAssetSrc = usePlatformImageGetAssetSrc();
    const getFlagSrc = getAssetSrc ? (countryCode: string) => getAssetSrc(`countryFlags/${countryCode}`) : undefined;
    const defaultPhoneNumber = customerValue?.toString() ?? '';

    const onChangeHandler = (event: React.ChangeEvent | null, newValue: string) => {
        onChange(newValue);
    };

    return (
        <ErrorBoundary fallback={fallback}>
            <Suspense fallback={fallback}>
                <PhoneInputField
                    portalContainerId={PORTAL_CONTAINER_ID}
                    getAssetSrc={getFlagSrc}
                    name={name}
                    label={label}
                    phoneNumber={defaultPhoneNumber}
                    onChange={onChangeHandler}
                    onBlur={onBlur}
                    required={required}
                    error={error != null ? <LocalizedText textKey={TextKey.ValidatePhoneNumber} /> : null}
                    data-test-id={dataTestId}
                />
            </Suspense>
        </ErrorBoundary>
    );
};
